import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import {
  Server,
  Database,
  Shield,
  HardDrive,
  Network,
  ArrowRight,
  Lock,
  CheckCircle,
  Menu,
  X,
  Layers,
  Globe,
  Users,
} from "lucide-react";
import { Logo } from "../components/Logo";
import { platformLogoMap } from "../config/platforms";

// ─── Sticky Navigation ────────────────────────────────────────────────────────

const navItems = [
  { label: "About",          id: "about" },
  { label: "What We Do",     id: "what-we-do" },
  { label: "Infrastructure", id: "infrastructure" },
  { label: "Software",       id: "software" },
  { label: "Contact",        id: "contact" },
];

function StickyNav() {
  const [menuOpen, setMenuOpen] = useState(false);

  function scrollTo(sectionId: string) {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  }

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 relative flex items-center justify-between h-20">

        {/* Left — Logo */}
        <div className="flex items-center flex-shrink-0">
          <img
            src="/logos/leogroup.png"
            alt="LEO Group"
            className="h-20 w-auto object-contain"
            onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
          />
        </div>

        {/* Center — Desktop nav (absolutely centered) */}
        <div className="hidden sm:flex absolute left-1/2 -translate-x-1/2 items-center gap-8">
          {navItems.map(({ label, id }) => (
            <button
              key={id}
              onClick={() => scrollTo(id)}
              className="relative text-sm font-medium text-[#101828] hover:text-gray-700 transition-colors whitespace-nowrap pb-0.5 border-b-2 border-transparent hover:border-gray-300"
            >
              {label}
            </button>
          ))}
        </div>

        {/* Right — mobile toggle */}
        <div className="flex items-center">
          <button
            className="sm:hidden text-[#101828] hover:text-gray-700 transition-colors"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div className="sm:hidden bg-white border-t border-gray-100 px-5 py-2">
          {navItems.map(({ label, id }) => (
            <button
              key={id}
              onClick={() => scrollTo(id)}
              className="block w-full text-left text-sm font-medium text-[#101828] hover:text-gray-700 py-3 border-b border-gray-50 last:border-0 transition-colors"
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </nav>
  );
}

// ─── Contact Section ──────────────────────────────────────────────────────────

const serviceOptions = [
  "Infrastructure",
  "General Inquiry",
];

function ContactSection() {
  const [form, setForm] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch("/api/contact-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, createdAt: new Date().toISOString() }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setSubmitted(true);
      setForm({});
    } catch (err) {
      setError(err instanceof Error ? err.message : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section id="contact" className="py-16 sm:py-24 bg-[#F9FAFB] scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">

        {/* Contact + Form — two-column */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">

          {/* Left — Info */}
          <div>
            <p className="text-xs uppercase tracking-widest text-[#7B1A1A] mb-4 font-semibold">Get In Touch</p>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#101828] mb-5 leading-tight">
              Contact Us
            </h2>
            <p className="text-[#364153] text-lg leading-relaxed mb-10">
              Get in touch with our team to discuss infrastructure, platforms, or enterprise services.
            </p>

            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-9 h-9 bg-[#7B1A1A]/8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-[#7B1A1A]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#1F2933] uppercase tracking-wide mb-1">Email</p>
                  <a href="mailto:info@leogroup.co.za" className="text-[#364153] hover:text-[#7B1A1A] transition-colors text-sm">
                    info@leogroup.co.za
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-9 h-9 bg-[#7B1A1A]/8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-[#7B1A1A]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#1F2933] uppercase tracking-wide mb-1">Office</p>
                  <a href="tel:+27121119164" className="text-[#364153] hover:text-[#7B1A1A] transition-colors text-sm">
                    +27 12 111 9164
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-9 h-9 bg-[#7B1A1A]/8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-[#7B1A1A]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#1F2933] uppercase tracking-wide mb-1">Find Us</p>
                  <address className="not-italic text-[#364153] text-sm leading-relaxed">
                    Waterford Court Office Park<br />
                    236 Glover Ave<br />
                    Die Hoewes<br />
                    Centurion, 0163
                  </address>
                </div>
              </div>
            </div>
          </div>

          {/* Right — Form */}
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-8 shadow-sm">
            {submitted ? (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-[#7B1A1A] mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-[#101828] mb-2">Request Received</h3>
                <p className="text-[#364153] max-w-sm mx-auto text-sm leading-relaxed">
                  Your message has been received. We will contact you shortly.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 text-sm text-[#7B1A1A] hover:underline"
                >
                  Submit another request
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="mb-2">
                  <h3 className="text-base font-semibold text-[#101828]">Send a Message</h3>
                  <p className="text-xs text-[#4B5563] mt-1">Complete the form below and our team will be in touch.</p>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  {[
                    { id: "name",    label: "Full Name",     placeholder: "Your full name",  required: true },
                    { id: "company", label: "Company Name",  placeholder: "Your company",    required: false },
                    { id: "email",   label: "Email Address", placeholder: "your@email.com",  required: true, type: "email" },
                    { id: "phone",   label: "Phone Number",  placeholder: "+27 …",           required: false },
                  ].map(({ id, label, placeholder, required, type }) => (
                    <div key={id}>
                      <label className="block text-xs font-semibold text-[#1F2933] uppercase tracking-wide mb-1.5">
                        {label}{required && <span className="text-[#7B1A1A] ml-0.5">*</span>}
                      </label>
                      <input
                        type={type ?? "text"}
                        name={id}
                        value={form[id] ?? ""}
                        onChange={handleChange}
                        placeholder={placeholder}
                        required={required}
                        className="w-full border border-[#E5E7EB] rounded-lg px-4 py-2.5 text-sm text-[#1F2933] placeholder-[#9CA3AF] focus:outline-none focus:border-[#7B1A1A] transition-colors"
                      />
                    </div>
                  ))}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#1F2933] uppercase tracking-wide mb-1.5">
                    Service Interest
                  </label>
                  <select
                    name="service"
                    value={form.service ?? ""}
                    onChange={handleChange}
                    className="w-full border border-[#E5E7EB] rounded-lg px-4 py-2.5 text-sm text-[#1F2933] focus:outline-none focus:border-[#7B1A1A] transition-colors bg-white"
                  >
                    <option value="">Select a service…</option>
                    {serviceOptions.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#1F2933] uppercase tracking-wide mb-1.5">
                    Message<span className="text-[#7B1A1A] ml-0.5">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={form.message ?? ""}
                    onChange={handleChange}
                    placeholder="How can we help you?"
                    rows={4}
                    required
                    className="w-full border border-[#E5E7EB] rounded-lg px-4 py-2.5 text-sm text-[#1F2933] placeholder-[#9CA3AF] focus:outline-none focus:border-[#7B1A1A] transition-colors resize-none"
                  />
                </div>

                {error && <p className="text-sm text-red-600">{error}</p>}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#7B1A1A] hover:bg-[#6b1515] disabled:opacity-50 text-white py-3 rounded-lg transition-colors text-sm font-medium"
                >
                  {submitting ? "Sending…" : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>

      </div>
    </section>
  );
}

// ─── Infrastructure Request Section ───────────────────────────────────────────

const infraTypes = [
  "Cloud Hosting",
  "Private Servers",
  "Hybrid Infrastructure",
  "Backup & Disaster Recovery",
  "AI Infrastructure",
];

function InfrastructureRequestForm({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch("/api/infrastructure-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setSubmitted(true);
      setForm({});
    } catch (err) {
      setError(err instanceof Error ? err.message : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="text-center py-8">
        <CheckCircle className="w-12 h-12 text-[#7B1A1A] mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-[#101828] mb-2">Request Submitted</h3>
        <p className="text-[#364153] max-w-sm mx-auto text-sm leading-relaxed">
          Your infrastructure request has been submitted. Our team will contact you shortly.
        </p>
        <div className="flex justify-center gap-4 mt-6">
          <button onClick={() => setSubmitted(false)} className="text-sm text-[#7B1A1A] hover:underline">
            Submit another
          </button>
          <button onClick={onClose} className="text-sm text-[#364153] hover:underline">
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-5">
        {[
          { id: "name",    label: "Full Name",     placeholder: "Your full name",  required: true },
          { id: "company", label: "Company Name",  placeholder: "Your company",    required: false },
          { id: "email",   label: "Email Address", placeholder: "your@email.com",  required: true, type: "email" },
          { id: "phone",   label: "Phone Number",  placeholder: "+27 …",           required: false },
        ].map(({ id, label, placeholder, required, type }) => (
          <div key={id}>
            <label className="block text-xs font-semibold text-[#1F2933] uppercase tracking-wide mb-1.5">
              {label}{required && <span className="text-[#7B1A1A] ml-0.5">*</span>}
            </label>
            <input
              type={type ?? "text"}
              name={id}
              value={form[id] ?? ""}
              onChange={handleChange}
              placeholder={placeholder}
              required={required}
              className="w-full border border-[#E5E7EB] rounded-lg px-4 py-2.5 text-sm text-[#1F2933] placeholder-[#9CA3AF] focus:outline-none focus:border-[#7B1A1A] transition-colors"
            />
          </div>
        ))}
      </div>

      <div>
        <label className="block text-xs font-semibold text-[#1F2933] uppercase tracking-wide mb-1.5">
          Infrastructure Type
        </label>
        <select
          name="type"
          value={form.type ?? ""}
          onChange={handleChange}
          className="w-full border border-[#E5E7EB] rounded-lg px-4 py-2.5 text-sm text-[#1F2933] focus:outline-none focus:border-[#7B1A1A] transition-colors bg-white"
        >
          <option value="">Select type…</option>
          {infraTypes.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      <div>
        <label className="block text-xs font-semibold text-[#1F2933] uppercase tracking-wide mb-1.5">
          Message
        </label>
        <textarea
          name="message"
          value={form.message ?? ""}
          onChange={handleChange}
          placeholder="Describe your infrastructure requirements…"
          rows={4}
          className="w-full border border-[#E5E7EB] rounded-lg px-4 py-2.5 text-sm text-[#1F2933] placeholder-[#9CA3AF] focus:outline-none focus:border-[#7B1A1A] transition-colors resize-none"
        />
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-[#7B1A1A] hover:bg-[#6b1515] disabled:opacity-50 text-white py-3 rounded-lg transition-colors text-sm font-medium"
      >
        {submitting ? "Submitting…" : "Submit Request"}
      </button>
    </form>
  );
}


export function PublicHomePage() {
  const navigate = useNavigate();
  const [infraModalOpen, setInfraModalOpen] = useState(false);

  useEffect(() => {
    document.title = "LEO Group Holding Company | Investment & Technology Holdings South Africa";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", "LEO Group Holding Company is a South African investment and technology holding company operating across fintech, infrastructure, software development, digital platforms, and marketing.");
  }, []);

  useEffect(() => {
    if (!infraModalOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setInfraModalOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [infraModalOpen]);

  const infrastructureServices = [
    {
      icon: Server,
      title: "Data Centre Hosting",
      description: "Secure, high-performance hosting environments with redundancy and uptime assurance.",
    },
    {
      icon: Database,
      title: "Backup & Disaster Recovery",
      description: "Automated backups and recovery strategies to ensure business continuity.",
    },
    {
      icon: Shield,
      title: "Network Security",
      description: "Advanced firewall, VPN, and monitoring systems to protect business data.",
    },
    {
      icon: HardDrive,
      title: "Cloud & Hybrid Solutions",
      description: "Flexible infrastructure combining on-premise and cloud environments.",
    },
    {
      icon: Network,
      title: "Server & Virtualisation Management",
      description: "Optimised server environments with full control and scalability.",
    },
  ];

  const platforms = [
    {
      name: "AutoGuard",
      description: "Security Operations & Workforce Management Platform",
    },
    {
      name: "MiCash",
      description: "Financial services and transaction platform",
    },
    {
      name: "Life Happens",
      description: "Life event planning and management services",
    },
    {
      name: "Get Me Printing",
      description: "On-demand printing and fulfillment solutions",
    },
  ];

  const licensedSolutions = [
    {
      name: "FPI",
      logo: "/logos/fpi.png",
      description: "Forensic and biometric identification solution for law enforcement and security operations",
    },
    {
      name: "BOS",
      logo: "/logos/bos.png",
      description: "Business operating system for streamlined workforce and operational management",
    },
    {
      name: "LSD",
      logo: "/logos/leomembership.png",
      description: "Lion Software Development platform for licensed software distribution and membership management",
    },
    {
      name: "PickMe",
      logo: "/logos/picme.png",
      description: "On-demand service marketplace connecting clients with verified service providers",
    },
    {
      name: "EvidenceWeb",
      logo: "/logos/evidenceweb.png",
      description: "Digital evidence management and case documentation system for legal and compliance teams",
    },
  ];

  const securityFeatures = [
    "ISO 27001-aligned framework implementation",
    "Continuous access monitoring and logging",
    "Data encryption and protection protocols",
    "Incident response and escalation procedures",
  ];

  return (
    <div className="min-h-screen bg-white">
      <StickyNav />

      {/* Hero Section */}
      <section
        className="relative min-h-screen flex flex-col"
        style={{
          backgroundImage:
            'url("https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=2560&q=85")',
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Multi-layer overlay — depth + brand tint */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#060d18]/92 via-[#0a1829]/88 to-[#060d18]/95" />
        <div className="absolute inset-0 bg-[#7B1A1A]/6" />

        {/* Hero Content */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="w-full max-w-4xl xl:max-w-5xl 2xl:max-w-6xl mx-auto">

            {/* Logo */}
            <div className="mb-8 sm:mb-10 flex justify-center">
              <Logo className="w-20 sm:w-28 lg:w-32 hover:opacity-80 transition-opacity" white />
            </div>

            {/* Eyebrow badge */}
            <div className="mb-5 sm:mb-7 flex justify-center">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/15 bg-white/6 text-white/55 text-[10px] sm:text-xs tracking-widest uppercase font-semibold">
                Technology Holding Company
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-white mb-5 sm:mb-6 leading-[1.1] font-semibold tracking-tight">
              Digital Infrastructure.<br className="hidden sm:block" /> Built to Scale.
            </h1>

            {/* Subheadline */}
            <p className="text-white/55 text-base sm:text-lg md:text-xl mb-10 sm:mb-12 max-w-md sm:max-w-xl md:max-w-2xl mx-auto leading-relaxed">
              We design, build, and operate enterprise-grade platforms and
              infrastructure for modern businesses.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-stretch sm:items-center">
              <button
                onClick={() =>
                  document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
                }
                className="w-full sm:w-auto min-w-[160px] bg-[#7B1A1A] hover:bg-[#6b1515] text-white px-8 py-3.5 rounded transition-colors font-medium text-sm sm:text-base"
              >
                Contact Us
              </button>
              <button
                onClick={() =>
                  document.getElementById("what-we-do")?.scrollIntoView({ behavior: "smooth" })
                }
                className="w-full sm:w-auto min-w-[160px] border border-white/30 text-white/75 px-8 py-3.5 rounded hover:bg-white/8 hover:text-white transition-colors text-sm sm:text-base"
              >
                View Overview
              </button>
            </div>

            {/* Caption */}
            <p className="mt-8 sm:mt-10 text-white/28 text-[10px] sm:text-xs tracking-widest uppercase">
              Private access · Qualified partners and stakeholders only
            </p>
          </div>
        </div>
      </section>

      {/* About LEO Group Section */}
      <section id="about" className="py-20 bg-[#F9FAFB] scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs uppercase tracking-widest text-[#7B1A1A] mb-4 font-semibold text-center">
            ABOUT LEO GROUP
          </p>
          <h2 className="text-3xl md:text-4xl font-semibold text-[#101828] text-center mb-6">
            About LEO Group
          </h2>
          <p className="text-gray-600 text-center max-w-2xl mx-auto mb-16 leading-relaxed">
            A technology holding company building, hosting, and managing innovative digital solutions across multiple industries.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-16">
            <div className="max-w-3xl space-y-5 text-[#364153] leading-relaxed">
              <p className="text-base">
                LEO Group is a technology holding company focused on building, hosting, and managing innovative digital solutions. Our approach combines enterprise infrastructure, internally developed platforms, and strategic technology partnerships to support modern businesses.
              </p>
              <p className="text-base">
                We do not operate as a traditional development agency. Instead, LEO Group creates and manages scalable technology ecosystems, providing businesses with secure, reliable, and future-ready solutions across multiple industries.
              </p>
              <p className="text-base">
                Our focus is on long-term value, operational efficiency, and the continuous evolution of technology systems that drive business growth.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {[
                { value: "ISO 27001", label: "Security Framework" },
                { value: "99.9%",     label: "Uptime Commitment" },
                { value: "4+",        label: "Proprietary Platforms" },
                { value: "24/7",      label: "Monitoring & Support" },
              ].map(({ value, label }) => (
                <div
                  key={label}
                  className="bg-white border border-[#E5E7EB] rounded-xl p-6 text-center shadow-sm hover:shadow-lg transition duration-300"
                >
                  <p className="text-2xl font-semibold text-[#7B1A1A] mb-1">{value}</p>
                  <p className="text-xs text-[#4A5565] uppercase tracking-wide font-medium">{label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {[
              "Technology-focused holding structure",
              "Managed infrastructure and hosting",
              "Innovation-driven platform development",
              "Long-term strategic partnerships",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 bg-white border border-[#E5E7EB] rounded-xl p-5 shadow-sm">
                <CheckCircle className="w-5 h-5 text-[#7B1A1A] flex-shrink-0 mt-0.5" />
                <span className="text-sm font-medium text-[#364153] leading-snug">{item}</span>
              </div>
            ))}
          </div>

          <div className="bg-[#101828] rounded-xl p-10 text-center">
            <h3 className="text-xl sm:text-2xl font-semibold text-white mb-3">
              Interested in Working With Us?
            </h3>
            <p className="text-white/65 mb-7 max-w-xl mx-auto leading-relaxed">
              Whether you need enterprise infrastructure, a technology partner, or access to our private platform — we would like to hear from you.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-[#7B1A1A] hover:bg-[#6b1515] text-white px-7 py-3 rounded transition-colors font-medium inline-flex items-center justify-center gap-2"
              >
                Get In Touch
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => document.getElementById("infrastructure")?.scrollIntoView({ behavior: "smooth" })}
                className="border border-white/30 text-white/80 px-7 py-3 rounded hover:bg-white/10 transition-colors font-medium"
              >
                View Infrastructure Services
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section id="what-we-do" className="py-20 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs uppercase tracking-widest text-[#7B1A1A] mb-4 font-semibold text-center">
            WHAT WE DO
          </p>
          <h2 className="text-3xl md:text-4xl font-semibold text-[#101828] text-center mb-6">
            Technology and Infrastructure Holding Company
          </h2>
          <p className="text-gray-600 text-center max-w-2xl mx-auto mb-12 leading-relaxed">
            LEO Group builds, operates, and maintains enterprise digital platforms while delivering
            infrastructure services to businesses that demand performance, security, and scale.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Layers,
                title: "Platform Development",
                body: "We design and operate proprietary digital platforms across financial services, security operations, and commercial sectors — built to scale and serve real market needs.",
              },
              {
                icon: Server,
                title: "Infrastructure Services",
                body: "Enterprise-grade hosting, backup, and network services delivered from our secure data centre environment — reliable, monitored, and compliance-ready.",
              },
              {
                icon: Globe,
                title: "Digital Ecosystem",
                body: "Our portfolio of owned platforms creates an interconnected ecosystem, enabling cross-platform efficiency and shared infrastructure that benefits every division.",
              },
              {
                icon: Shield,
                title: "Security & Compliance",
                body: "Information security is embedded across all systems. We operate under an ISO 27001-aligned framework with continuous monitoring and structured access controls.",
              },
              {
                icon: Network,
                title: "System Integration",
                body: "We connect platforms, data, and external services into cohesive environments — enabling automation, real-time visibility, and operational efficiency.",
              },
              {
                icon: Users,
                title: "Enterprise Partnerships",
                body: "We work with organisations that require reliable, long-term infrastructure and platform relationships — providing dedicated support and tailored solutions.",
              },
            ].map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="bg-white border border-[#E5E7EB] rounded-xl p-6 shadow-sm hover:shadow-lg transition duration-300 hover:border-[#7B1A1A]/30"
              >
                <div className="w-11 h-11 bg-[#7B1A1A]/8 rounded-lg flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[#7B1A1A]" />
                </div>
                <h3 className="text-base font-semibold text-[#101828] mb-2">{title}</h3>
                <p className="text-sm text-[#4A5565] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Infrastructure & Hosting Section */}
      <section id="infrastructure" className="py-12 sm:py-20 bg-[#F9FAFB] scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-widest text-[#7B1A1A] mb-4 font-semibold">
              INFRASTRUCTURE & HOSTING
            </p>
            <h2 className="text-2xl sm:text-4xl text-[#101828] mb-5 font-semibold leading-tight">
              Enterprise Infrastructure Services
            </h2>
            <p className="text-[#364153] text-lg max-w-3xl mx-auto leading-relaxed">
              We provide enterprise-grade infrastructure solutions designed for performance, security, and scalability across all business environments.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {infrastructureServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl border border-[#E5E7EB] hover:border-[#7B1A1A]/30 hover:shadow-md transition-all"
                >
                  <div className="w-10 h-10 bg-[#7B1A1A]/8 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[#7B1A1A]" />
                  </div>
                  <h3 className="text-base text-[#101828] mb-2 font-semibold">
                    {service.title}
                  </h3>
                  <p className="text-[#4A5565] text-sm leading-relaxed">
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Trust line */}
          <div className="flex items-center justify-center gap-2 mb-12">
            <Shield className="w-4 h-4 text-[#7B1A1A] flex-shrink-0" />
            <p className="text-[#4A5565] text-sm text-center">
              Built on secure, scalable infrastructure aligned with enterprise standards and best practices.
            </p>
          </div>

          <div className="bg-[#7B1A1A] text-white p-10 rounded-xl text-center">
            <h3 className="text-2xl mb-4 font-semibold">
              Host Your Infrastructure With Us
            </h3>
            <p className="text-white/75 mb-6 max-w-2xl mx-auto leading-relaxed">
              Secure, monitored, and compliance-ready infrastructure tailored to
              your business needs. From server hosting to disaster recovery, we
              provide the foundation for your digital operations.
            </p>
            <button
              onClick={() => setInfraModalOpen(true)}
              className="border border-white/50 text-white px-8 py-3 rounded hover:bg-white/10 transition-colors inline-flex items-center gap-2"
            >
              Infrastructure Information
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Infrastructure Enquiry Modal */}
      {infraModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setInfraModalOpen(false); }}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-8 pt-7 pb-5 border-b border-[#E5E7EB]">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#7B1A1A] font-semibold mb-1">Get In Touch</p>
                <h2 className="text-xl font-semibold text-[#101828]">Infrastructure Enquiry</h2>
              </div>
              <button
                onClick={() => setInfraModalOpen(false)}
                className="text-[#9CA3AF] hover:text-[#101828] transition-colors p-1"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-8 py-7">
              <InfrastructureRequestForm onClose={() => setInfraModalOpen(false)} />
            </div>
          </div>
        </div>
      )}

      {/* Split Layout Section */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center">
            <div className="relative w-full h-72 md:h-full rounded-xl overflow-hidden">
              <img
                src="/images/close-up-of-desktop-with-laptop-and-abstract-codin-2026-03-26-05-00-25-utc.jpg"
                alt="Infrastructure"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-[#101828]/50" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-[#7B1A1A] mb-4 font-semibold">
                Built for Enterprise
              </p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#101828] mb-4 leading-tight">
                Built for Performance. Secured for Scale.
              </h2>
              <p className="text-[#364153] mb-6 leading-relaxed">
                We design and operate secure, scalable infrastructure environments that support mission-critical business operations across multiple industries.
              </p>
              <ul className="space-y-3">
                {[
                  "Enterprise-grade hosting environments",
                  "Secure data storage and compliance-ready systems",
                  "Scalable infrastructure for growing businesses",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-[#364153]">
                    <CheckCircle className="w-5 h-5 text-[#7B1A1A] flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Ecosystem Section */}
      <section id="software" className="py-12 sm:py-20 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-widest text-[#7B1A1A] mb-4 font-semibold">
              PLATFORM ECOSYSTEM
            </p>
            <h2 className="text-2xl sm:text-3xl text-[#101828] mb-6 font-semibold">
              Proprietary Digital Platforms
            </h2>
            <p className="text-[#364153] text-lg max-w-3xl mx-auto">
              LEO Group owns and operates a portfolio of specialized digital
              platforms serving various market sectors.
            </p>
          </div>

          <div className="platform-grid grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {platforms.map((platform, index) => {
              const logoSrc = platformLogoMap[platform.name];
              return (
                <div
                  key={index}
                  className="platform-card bg-[#F9FAFB] p-6 rounded-lg border border-[#E5E7EB] text-center"
                >
                  {logoSrc && (
                    <div className="flex items-center justify-center h-24 mb-3">
                      <img
                        src={logoSrc}
                        alt={platform.name}
                        className={`platform-card-logo ${platform.name === "Life Happens" ? "max-h-14" : "max-h-16"} w-auto object-contain`}
                        onError={(e) => { e.currentTarget.src = "/logo.png"; }}
                      />
                    </div>
                  )}
                  <h3 className="text-sm text-[#101828] mb-2 font-semibold">
                    {platform.name}
                  </h3>
                  <p className="text-[#4A5565] text-xs leading-relaxed">
                    {platform.description}
                  </p>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* Licensed Solutions Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-white to-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-widest text-[#7B1A1A] mb-3 font-semibold">
              Licensed Solutions
            </p>
            <h2 className="text-2xl sm:text-3xl text-[#101828] mb-4 font-semibold">
              Licensed Software Portfolio
            </h2>
            <p className="text-[#364153] text-base max-w-2xl mx-auto leading-relaxed">
              LEO Group holds distribution and licensing rights to a suite of
              specialised third-party software solutions deployed across multiple sectors.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
            {licensedSolutions.map((solution) => (
              <div
                key={solution.name}
                className="group flex flex-col bg-white rounded-xl border border-[#E5E7EB] p-6 text-center
                           shadow-[0_2px_8px_rgba(0,0,0,0.06)]
                           hover:shadow-[0_10px_25px_rgba(0,0,0,0.08)]
                           hover:-translate-y-[5px] hover:scale-[1.03]
                           hover:border-[#7B1A1A]/20
                           transition-all duration-300 ease-out cursor-default"
              >
                {/* Logo container — fixed 90px height, perfectly centred, no distortion */}
                <div
                  className="flex items-center justify-center mx-auto mb-5"
                  style={{ height: "90px", width: "100%" }}
                >
                  <img
                    src={solution.logo}
                    alt={solution.name}
                    style={{
                      maxHeight: "72px",
                      maxWidth: "80%",
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                      display: "block",
                      opacity: 0.9,
                      transition: "opacity 0.3s ease",
                    }}
                    className="group-hover:opacity-100"
                    onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                  />
                </div>

                {/* Divider */}
                <div className="w-8 h-px bg-[#E5E7EB] group-hover:bg-[#7B1A1A]/30 mx-auto mb-4 transition-colors duration-300" />

                {/* Title — fixed line-height so all titles sit at the same Y */}
                <h3 className="text-sm font-semibold text-[#101828] tracking-wide leading-tight mb-2 min-h-[1.25rem]">
                  {solution.name}
                </h3>

                {/* Description — flex-1 fills remaining card height so all cards stretch equally */}
                <p className="text-[#6B7280] text-xs leading-relaxed flex-1">
                  {solution.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security & Compliance Section */}
      <section className="py-12 sm:py-20 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-xs uppercase tracking-widest text-[#7B1A1A] mb-4 font-semibold">
                SECURITY & COMPLIANCE
              </p>
              <h2 className="text-2xl sm:text-3xl text-[#101828] mb-6 font-semibold">
                Information Security as a Foundational Principle
              </h2>
              <p className="text-[#364153] text-lg leading-relaxed">
                LEO Group prioritises the protection of data, systems, and
                infrastructure through structured security practices aligned
                with internationally recognised standards.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg border border-[#E5E7EB]">
              <h3 className="text-xl text-[#101828] mb-4 font-semibold">
                Security Framework Status
              </h3>
              <p className="text-[#364153] mb-6 leading-relaxed">
                The organisation is currently in the process of implementing and
                formalising an ISO 27001-aligned information security management
                framework. While certification is in progress, security
                principles are already embedded across all systems,
                infrastructure, and development practices.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {securityFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#7B1A1A] mt-0.5 flex-shrink-0" />
                    <span className="text-[#364153] text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <ContactSection />

      {/* Footer */}
      <footer className="bg-[#6b1515] mt-8 sm:mt-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-12 sm:py-16">

          {/* 3-column grid — brand · quick links · legal */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-8 mb-10 sm:mb-12 text-center sm:text-left">

            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4 justify-center sm:justify-start">
                <Logo className="w-14" white />
                <span className="text-sm font-semibold text-white">Leo Group</span>
              </div>
              <p className="text-white/50 text-xs leading-relaxed">
                Technology and infrastructure holding company operating across
                Africa and emerging markets.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-4">Quick Links</p>
              <ul className="space-y-3 text-sm text-white/60">
                <li
                  className="hover:text-white cursor-pointer transition-colors"
                  onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })}
                >
                  About Leo Group
                </li>
                <li
                  className="hover:text-white cursor-pointer transition-colors"
                  onClick={() => document.getElementById("what-we-do")?.scrollIntoView({ behavior: "smooth" })}
                >
                  What We Do
                </li>
                <li
                  className="hover:text-white cursor-pointer transition-colors"
                  onClick={() => document.getElementById("infrastructure")?.scrollIntoView({ behavior: "smooth" })}
                >
                  Infrastructure Services
                </li>
                <li
                  className="hover:text-white cursor-pointer transition-colors"
                  onClick={() => document.getElementById("software")?.scrollIntoView({ behavior: "smooth" })}
                >
                  Software Platforms
                </li>
                <li
                  className="hover:text-white cursor-pointer transition-colors"
                  onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                >
                  Contact Us
                </li>
              </ul>
            </div>

            {/* Legal & Compliance */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-4">Legal &amp; Compliance</p>
              <ul className="space-y-3 text-sm text-white/60">
                <li className="hover:text-white cursor-pointer transition-colors" onClick={() => navigate("/terms")}>
                  Terms &amp; Conditions
                </li>
                <li className="hover:text-white cursor-pointer transition-colors" onClick={() => navigate("/privacy")}>
                  Privacy Policy
                </li>
                <li className="hover:text-white cursor-pointer transition-colors" onClick={() => navigate("/compliance")}>
                  POPIA Compliance
                </li>
              </ul>
            </div>

          </div>

          {/* Bottom bar */}
          <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/30">
            <p>&copy; {new Date().getFullYear()} LEO Group Holding Company. All rights reserved.</p>
            <p>
              Designed &amp; developed by{" "}
              <a
                href="https://www.beyondic.co.za"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white/60 transition-colors underline underline-offset-2"
              >
                Beyond Imagination
              </a>
            </p>
          </div>

        </div>
      </footer>
    </div>
  );
}
